// @ts-nocheck
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { URL } from "../../service/Service";
import ErrorMessage from "../../components/ErrorMessage";
import { useTranslation } from "../../i18n/LanguageContext";

function EachBalance() {
  const { t } = useTranslation();
  const [showBalance, setShowBalance] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [balanceData, setBalanceData] = useState({ balance: 0 });
  const [errorMessage, setErrorMessage] = useState("");

  const [jobStatus, setJobStatus] = useState("IDLE"); // 'IDLE', 'PROCESSING', 'COMPLETED', 'ERROR'

  const jwtToken = sessionStorage.getItem("jwt");
  const { id } = useParams();

  const fetchBalanceData = useCallback(async () => {
    try {
      const response = await fetch(`${URL}/api/partners/${id}/balance`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setBalanceData(data);
      }
    } catch (error) {
      console.error('Error fetching balance data:', error);
    } finally {
      setLoadingBalance(false);
    }
  }, [id, jwtToken]);

  const checkJobStatus = useCallback(async () => {
    try {
      const response = await fetch(`${URL}/api/syncPartnerTransactions/${id}`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });

      if (response.ok) {
        const result = await response.json(); // Expected: { status: "PROCESSING", ... }

        // Only update state if it changed to avoid unnecessary re-renders
        setJobStatus((prevStatus) => {
          if (prevStatus !== result.status) {
            if (result.status === 'COMPLETED') {
              fetchBalanceData();
            }
            return result.status;
          }
          return prevStatus;
        });
      } else if (response.status === 404) {
        // No job running
        setJobStatus("IDLE");
      }
    } catch (error) {
      console.error("Error checking status:", error);
    }
  }, [id, jwtToken, fetchBalanceData]);

  const triggerTotalCalculation = async () => {
    setJobStatus("PROCESSING");
    try {
      const response = await fetch(`${URL}/api/${id}/triggerTotalCalculation`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      if (response.status === 409) {
        console.log("Job already running, connecting to it...");
        await checkJobStatus();
      } else if (!response.ok && response.status !== 202) {
        setJobStatus("ERROR");
        setErrorMessage(t('balance.updateError'));
      }
    } catch (error) {
      console.error("Error triggering sync:", error);
      setJobStatus("ERROR");
      setErrorMessage(t('balance.updateError'));
    }
  };

  useEffect(() => {
    fetchBalanceData();
    checkJobStatus();
  }, [fetchBalanceData, checkJobStatus]);

  useEffect(() => {
    let intervalId;

    if (jobStatus === "PROCESSING") {
      intervalId = setInterval(() => {
        checkJobStatus();
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [jobStatus, checkJobStatus]);

  // Toggle balance visibility
  const handleToggle = () => {
    const newShowBalance = !showBalance;
    setShowBalance(newShowBalance);
    localStorage.setItem("showBalance", JSON.stringify(newShowBalance));
  };

  // Helper to determine if button should be disabled
  const isSyncing = jobStatus === "PROCESSING";

  return (
		<div className="ml-4 flex flex-col justify-end gap-3 sm:flex-row sm:items-stretch sm:gap-4">
		<ErrorMessage message={errorMessage} />
		<div className="relative inline-flex">
		  <button
			  type="button"
			  className="inline-flex min-h-[46px] w-full items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-wait disabled:bg-gray-50 sm:min-w-[190px]"
			  onClick={handleToggle}
			  disabled={loadingBalance}
			  aria-pressed={showBalance}
		  >
			<span className="font-bold">{t('balance.account')}</span>
			{loadingBalance ? (
				<ArrowPathIcon className="h-4 w-4 animate-spin text-green-cl" />
			) : (
				<span className={`whitespace-nowrap text-xs ${showBalance ? "font-bold text-green-cl" : "font-normal text-gray-400"}`}>
				  {showBalance
					  ? balanceData.balance === 0
						  ? "0,00\u00A0€"
						  : `${(Math.ceil(balanceData.balance * 100) / 100).toFixed(2).replace('.', ',')}\u00A0€`
					  : t('balance.show')}
			</span>
			)}
		  </button>
		</div>

		<button
			className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border border-green-cl bg-white px-4 py-2.5 text-sm font-bold text-green-cl shadow-sm transition hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
			onClick={triggerTotalCalculation}
			disabled={isSyncing}
		>
		  <ArrowPathIcon className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
		  {isSyncing ? t('balance.updating') : jobStatus === 'COMPLETED' ? t('balance.updated') : t('balance.update')}
		</button>
      </div>
  );
}

export default EachBalance;
